import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Backdrop,
  IconButton,
  TablePagination,
} from '@mui/material';
import { toast } from 'react-toastify';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';

const getArray = (data) => (Array.isArray(data) ? data : []);

const getCurrentUserName = () => {
  const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (!rawUser) return '';

  try {
    const parsedUser = JSON.parse(rawUser);
    return parsedUser?.name || parsedUser?.username || '';
  } catch {
    return '';
  }
};

const normalizeBasketNode = (node) => ({
  text: node?.text || '',
  data: { ...(node?.data || {}) },
  children: Array.isArray(node?.children) ? node.children.map(normalizeBasketNode) : [],
  state: node?.state,
});

const buildDrugNode = (drugDetail, userName) => ({
  text: drugDetail.drugName,
  data: {
    id: drugDetail.drugId,
    contentVisible: false,
    ndc: drugDetail.ndc,
    manufacturer: drugDetail.manufacturer,
    pgnKey: drugDetail.pgnKey,
    pgnDesc: drugDetail.pgnDesc,
    action: 'ADD',
    hierarchyLevel: 'drug',
    createdBy: userName,
    keyCompetitor: drugDetail.keyCompetitor,
    clientDrugFlag: drugDetail.clientDrugFlag,
    drugDosageFactor: drugDetail.drugDosageFactor ?? null,
    drugIndicationFactor: drugDetail.drugIndicationFactor ?? null,
    rxType: drugDetail.rxType || '',
    spclGrp: drugDetail.spclGrp || '',
    startDate: drugDetail.startDate || null,
    timeperiodType: drugDetail.timeperiodType || '',
    factors: Array.isArray(drugDetail.factors) ? drugDetail.factors : [],
  },
  children: [],
});

const buildBrandNode = (brand, userName) => ({
  text: brand.brandName,
  data: {
    id: brand.brandId,
    action: 'ADD',
    hierarchyLevel: 'brand',
    createdBy: userName,
  },
  children: (brand.formTlc3Details || []).map((detail) => ({
    text: detail.formTlc3,
    data: {
      action: 'ADD',
      hierarchyLevel: 'formTlc3',
      isExcluded: false,
      createdBy: userName,
    },
    children: (detail.drugDetails || []).map((drugDetail) => buildDrugNode(drugDetail, userName)),
  })),
});

const buildFormTlc3Node = (detail, userName, extraData = {}) => ({
  text: detail.formTlc3,
  data: {
    action: 'ADD',
    hierarchyLevel: 'formTlc3',
    isExcluded: false,
    createdBy: userName,
    ...extraData,
  },
  children: (detail.drugDetails || []).map((drugDetail) => buildDrugNode(drugDetail, userName)),
});

const findNode = (node, nodeId) => {
  if (!node) return null;
  const currentId = String(node.data?.id ?? node.text);
  if (currentId === String(nodeId)) return node;
  for (const child of node.children || []) {
    const match = findNode(child, nodeId);
    if (match) return match;
  }
  return null;
};

const findParentNode = (node, nodeId, parent = null) => {
  if (!node) return null;
  const currentId = String(node.data?.id ?? node.text);
  if (currentId === String(nodeId)) return parent;
  for (const child of node.children || []) {
    const match = findParentNode(child, nodeId, node);
    if (match) return match;
  }
  return null;
};

const updateNodeChildren = (node, nodeId, updater) => {
  const currentId = String(node.data?.id ?? node.text);
  if (currentId === String(nodeId)) {
    return updater(node);
  }
  return {
    ...node,
    children: (node.children || []).map((child) => updateNodeChildren(child, nodeId, updater)),
  };
};

const removeNode = (node, nodeId) => ({
  ...node,
  children: (node.children || [])
    .filter((child) => String(child.data?.id ?? child.text) !== String(nodeId))
    .map((child) => removeNode(child, nodeId)),
});

const updateNodeData = (node, nodeId, updater) => {
  const currentId = String(node.data?.id ?? node.text);
  if (currentId === String(nodeId)) {
    return {
      ...node,
      data: updater(node.data || {}),
    };
  }

  return {
    ...node,
    children: (node.children || []).map((child) => updateNodeData(child, nodeId, updater)),
  };
};

const updateNodeText = (node, nodeId, newText, userName) => {
  const currentId = String(node.data?.id ?? node.text);
  if (currentId === String(nodeId)) {
    return {
      ...node,
      text: newText,
      data: {
        ...node.data,
        action: node.data?.action || 'UPDATE',
        updatedBy: userName || node.data?.updatedBy,
      },
    };
  }

  return {
    ...node,
    children: (node.children || []).map((child) => updateNodeText(child, nodeId, newText, userName)),
  };
};

const flattenBrands = (node, result = []) => {
  if (!node) return result;
  if (node.data?.hierarchyLevel === 'brand') {
    result.push(node.text);
  }
  (node.children || []).forEach((child) => flattenBrands(child, result));
  return result;
};

const prepareBasketForSubmit = (node) => {
  const data = { ...(node?.data || {}) };

  if (data.hierarchyLevel?.includes('classLevel') && data.action === 'ADD') {
    delete data.id;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'isExcluded')) {
    data.excluded = data.isExcluded;
    delete data.isExcluded;
  }

  if (data.hierarchyLevel === 'drug') {
    data.keyCompetitor = data.keyCompetitor === true || data.keyCompetitor === 'true';
    data.clientDrugFlag = data.clientDrugFlag === true || data.clientDrugFlag === 'true';
  }

  delete data.contentVisible;

  return {
    text: node?.text || '',
    data,
    children: Array.isArray(node?.children) ? node.children.map(prepareBasketForSubmit) : [],
  };
};

const collectExpandedIds = (node, result = []) => {
  if (!node || !Array.isArray(node.children) || node.children.length === 0) return result;
  result.push(String(node.data?.id ?? node.text));
  node.children.forEach((child) => collectExpandedIds(child, result));
  return result;
};

const collectEmptyClassNames = (node, result = []) => {
  if (!node) return result;

  if (
    node.data?.hierarchyLevel?.includes('classLevel')
    && Array.isArray(node.children)
    && node.children.length === 0
  ) {
    result.push(node.text);
  }

  (node.children || []).forEach((child) => collectEmptyClassNames(child, result));
  return result;
};

const mergeStyleButtonBaseSx = {
  textTransform: 'none',
  fontWeight: 600,
  px: 3,
  py: 1,
  borderRadius: '8px',
  boxShadow: 'none',
  
  transition: 'all 0.2s',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
};

const mergeStyleSecondaryButtonSx = {
  ...mergeStyleButtonBaseSx,
  color: '#64748b',
  borderColor: '#e2e8f0',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#cbd5e1',
  },
};

const mergeStylePrimaryButtonSx = {
  ...mergeStyleButtonBaseSx,
  backgroundColor: '#1E88E5',
  color: '#fff',
  boxShadow: '0 2px 4px rgba(30, 136, 229, 0.2)',
  '&:hover': {
    backgroundColor: '#1976d2',
    boxShadow: '0 4px 8px rgba(30, 136, 229, 0.3)',
  },
};

const mergeStyleInfoButtonSx = {
  ...mergeStyleButtonBaseSx,
  backgroundColor: '#e0f2fe',
  color: '#1E88E5',
  '&:hover': {
    backgroundColor: '#bae6fd',
  },
};

const getNodeIconColor = (hierarchyLevel) => {
  switch (hierarchyLevel) {
    case 'market':
    case 'classLevel1':
    case 'classLevel2':
    case 'brand':
      return '#f4b400';
    case 'formTlc3':
      return '#14b8a6';
    case 'drug':
      return '#ef4444';
    default:
      return '#f4b400';
  }
};

const getNodeIcon = (hierarchyLevel, isExpanded) => {
  const iconColor = getNodeIconColor(hierarchyLevel);

  switch (hierarchyLevel) {
    case 'market':
    case 'classLevel1':
    case 'classLevel2':
    case 'brand':
      return isExpanded
        ? <FolderIcon fontSize="small" sx={{ color: iconColor }} />
        : <FolderIcon fontSize="small" sx={{ color: iconColor }} />;
    case 'formTlc3':
      return <Inventory2OutlinedIcon fontSize="small" sx={{ color: iconColor }} />;
    case 'drug':
      return <MedicationOutlinedIcon fontSize="small" sx={{ color: iconColor }} />;
    default:
      return isExpanded
        ? <FolderOpenIcon fontSize="small" sx={{ color: iconColor }} />
        : <FolderIcon fontSize="small" sx={{ color: iconColor }} />;
  }
};

const formatHierarchyLabel = (hierarchyLevel) => {
  if (!hierarchyLevel) return 'node';
  if (hierarchyLevel === 'formTlc3') return 'form TLC3';
  if (hierarchyLevel === 'classLevel1') return 'class level 1';
  if (hierarchyLevel === 'classLevel2') return 'class level 2';
  return hierarchyLevel;
};

const getVisibleTreeChildren = (node) => (node.children || []).filter((child) => child.data?.hierarchyLevel !== 'drug');

const marketDialogTitleSx = {
  px: 3,
  py: 2.5,
  pb: 1.5,
  borderBottom: '1px solid #f1f5f9',
  fontWeight: 600,
  color: '#1e293b',
};

const marketDialogActionsSx = {
  px: 3,
  py: 2.5,
  borderTop: '1px solid #f1f5f9',
};

const getMonthLabel = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', { month: 'long' });
};

const getYearLabel = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return String(date.getFullYear());
};

const renderEllipsisCell = (value, maxWidth) => {
  const displayValue = value || '-';

  return (
    <Tooltip title={displayValue} placement="top" arrow>
      <Box
        sx={{
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayValue}
      </Box>
    </Tooltip>
  );
};

const factorColumns = [
  { header: 'Specialty Group', id: 'spclGrp', accessorFn: (row) => row.spclGrp || 'UNSPECIFIED', size: 180 },
  { header: 'Rx Type', id: 'rxType', accessorFn: (row) => row.rxType || 'UNSPECIFIED', size: 120 },
  { header: 'Dosage Factor', id: 'drugDosageFactor', accessorFn: (row) => row.drugDosageFactor ?? '-', size: 140 },
  { header: 'Indication Factor', id: 'drugIndicationFactor', accessorFn: (row) => row.drugIndicationFactor ?? '-', size: 150 },
  { header: 'Time Period Type', id: 'timeperiodType', accessorFn: (row) => row.timeperiodType || 'UNSPECIFIED', size: 150 },
  { header: 'Start Date(Month)', id: 'startDateMonth', accessorFn: (row) => getMonthLabel(row.startDate), size: 150 },
  { header: 'Start Date(Year)', id: 'startDateYear', accessorFn: (row) => getYearLabel(row.startDate), size: 140 },
];

const BasketNode = ({ node, level, selectedNodeId, onSelect, expandedNodeIds, onToggleExpand, isLast }) => {
  const nodeId = String(node.data?.id ?? node.text);
  const isSelected = nodeId === String(selectedNodeId);
  const visibleChildren = getVisibleTreeChildren(node);
  const hasChildren = visibleChildren.length > 0;
  const isExpanded = hasChildren ? expandedNodeIds.includes(nodeId) : false;

  return (
    <Box sx={{ position: 'relative', pl: level === 0 ? 0 : 3 }}>
      {level > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            left: 10,
            top: 0,
            bottom: isLast ? '24px' : 0,
            borderLeft: '1px dashed',
            borderColor: 'divider',
          }}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 34,
          px: 1,
          py: 0.5,
          ml: level === 0 ? 0 : 0.5,
          borderRadius: 1.5,
          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.10)' : 'transparent',
          '&:hover': {
            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.10)' : 'action.hover',
          },
        }}
      >
        {level > 0 ? (
          <Box
            sx={{
              position: 'absolute',
              left: -2,
              top: '50%',
              width: 12,
              borderTop: '1px dashed',
              borderColor: 'divider',
            }}
          />
        ) : null}
        <IconButton
          size="small"
          onClick={() => {
            if (hasChildren) onToggleExpand(nodeId);
          }}
          sx={{
            width: 22,
            height: 22,
            visibility: hasChildren ? 'visible' : 'hidden',
            color: 'text.secondary',
          }}
        >
          {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getNodeIcon(node.data?.hierarchyLevel, isExpanded)}
        </Box>
        <Box
          onClick={(event) => onSelect(event, nodeId)}
          sx={{
            minWidth: 0,
            flex: 1,
            cursor: 'pointer',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
            {node.text}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatHierarchyLabel(node.data?.hierarchyLevel)}
          </Typography>
        </Box>
      </Box>

      {hasChildren && isExpanded ? (
        <Box sx={{ mt: 0.25 }}>
          {visibleChildren.map((child, index) => (
            <BasketNode
              key={`${child.data?.id ?? child.text}-${level}`}
              node={child}
              level={level + 1}
              selectedNodeId={selectedNodeId}
              onSelect={onSelect}
              expandedNodeIds={expandedNodeIds}
              onToggleExpand={onToggleExpand}
              isLast={index === visibleChildren.length - 1}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

BasketNode.propTypes = {
  node: PropTypes.shape({
    text: PropTypes.string,
    data: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      hierarchyLevel: PropTypes.string,
    }),
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  level: PropTypes.number.isRequired,
  selectedNodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  expandedNodeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  isLast: PropTypes.bool,
};

const NestedFormTlcList = ({ brand, formTlc3Details, selectedBrandIds, selectedFormTlcs, toggleFormTlcSelection }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedDetails = formTlc3Details.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ px: 2.5, py: 1.5, backgroundColor: 'grey.50' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Form TLC
      </Typography>
      <Stack spacing={0.75}>
        {paginatedDetails.map((detail) => (
          <Stack direction="row" alignItems="center" spacing={1} key={`${brand.brandId}-${detail.formTlc3}`}>
            <input
              type="checkbox"
              checked={selectedFormTlcs[brand.brandId]?.includes(detail.formTlc3) || selectedBrandIds.includes(brand.brandId)}
              onChange={() => toggleFormTlcSelection(brand, detail.formTlc3)}
            />
            <Typography variant="body2" color="text.secondary">
              {detail.formTlc3}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {formTlc3Details.length > 10 && (
        <TablePagination
          component="div"
          count={formTlc3Details.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderBottom: 'none', '.MuiTablePagination-toolbar': { minHeight: 40, paddingLeft: 0 } }}
        />
      )}
    </Box>
  );
};

NestedFormTlcList.propTypes = {
  brand: PropTypes.object.isRequired,
  formTlc3Details: PropTypes.array.isRequired,
  selectedBrandIds: PropTypes.array.isRequired,
  selectedFormTlcs: PropTypes.object.isRequired,
  toggleFormTlcSelection: PropTypes.func.isRequired,
};

const MarketBaskets = () => {
  const theme = useTheme();
  const { error, execute } = useAPICall();
  const [userName] = useState(() => getCurrentUserName());
  const [markets, setMarkets] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedMarketId, setSelectedMarketId] = useState('');
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [selectedFormTlcs, setSelectedFormTlcs] = useState({});
  const [brandTablePage, setBrandTablePage] = useState(1);
  const [brandTablePageSize, setBrandTablePageSize] = useState(10);
  const [basketTree, setBasketTree] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [expandedNodeIds, setExpandedNodeIds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drugDetailsOpen, setDrugDetailsOpen] = useState(false);
  const [newMarket, setNewMarket] = useState({ marketName: '', marketType: '' });
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingBasket, setLoadingBasket] = useState(false);
  const [refreshingLists, setRefreshingLists] = useState(false);
  const [creatingMarket, setCreatingMarket] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [nodeDialogMode, setNodeDialogMode] = useState('create');
  const [nodeNameDraft, setNodeNameDraft] = useState('');
  const [nodeMenuAnchorEl, setNodeMenuAnchorEl] = useState(null);

  const selectedMarket = useMemo(
    () => markets.find((market) => String(market.marketId) === String(selectedMarketId)) || null,
    [markets, selectedMarketId]
  );

  const selectedNode = useMemo(
    () => (basketTree && selectedNodeId ? findNode(basketTree, selectedNodeId) : null),
    [basketTree, selectedNodeId]
  );
  const selectedBrandDrugGroups = useMemo(
    () => (selectedNode?.data?.hierarchyLevel === 'brand' ? selectedNode.children || [] : []),
    [selectedNode]
  );

  const canCreateChildNode = useCallback((node) => {
    if (!node) return false;

    const level = node.data?.hierarchyLevel;
    if (level === 'brand' || level === 'drug' || level === 'formTlc3') {
      return false;
    }

    if (level?.includes('classLevel') && level === 'classLevel3') {
      return false;
    }

    return true;
  }, []);

  const canRenameClassNode = useCallback((node) => Boolean(node?.data?.hierarchyLevel?.includes('classLevel')), []);
  const canDeleteNode = useCallback((node) => {
    const level = node?.data?.hierarchyLevel;
    return Boolean(node) && level !== 'market' && level !== 'drug';
  }, []);

  const loadMarketsAndBrands = useCallback(async () => {
    setRefreshingLists(true);
    try {
      const [marketResponse, brandResponse] = await Promise.all([
        execute(API_CONFIG.marketBaskets.getMarkets, null, {
          errorMessage: 'Unable to load markets.',
          showErrorToast: false,
        }),
        execute(API_CONFIG.marketBaskets.getBrands, null, {
          errorMessage: 'Unable to load brands.',
          showErrorToast: false,
        }),
      ]);

      setMarkets(getArray(marketResponse));
      setBrands(getArray(brandResponse));
    } catch {
    } finally {
      setRefreshingLists(false);
      setLoadingPage(false);
    }
  }, [execute]);

  useEffect(() => {
    loadMarketsAndBrands();
  }, [loadMarketsAndBrands]);

  useEffect(() => {
    const loadBasket = async () => {
      if (!selectedMarketId) {
        setBasketTree(null);
        setSelectedNodeId('');
        setLoadingBasket(false);
        return;
      }

      setLoadingBasket(true);
      try {
        const response = await execute(API_CONFIG.marketBaskets.getMarketBasket, null, {
          params: { market_id: selectedMarketId },
          errorMessage: 'Unable to load market basket.',
          showErrorToast: false,
        });
        const normalized = normalizeBasketNode(response);
        setBasketTree(normalized);
        setSelectedNodeId(String(normalized.data?.id ?? normalized.text));
        setExpandedNodeIds(collectExpandedIds(normalized, []));
      } catch {
      } finally {
        setLoadingBasket(false);
      }
    };

    loadBasket();
  }, [execute, selectedMarketId]);

  const toggleBrandSelection = (brandId) => {
    setSelectedBrandIds((prev) => (
      prev.includes(brandId) ? prev.filter((id) => id !== brandId) : [...prev, brandId]
    ));
  };

  const toggleFormTlcSelection = (brand, formTlc3) => {
    const brandId = brand.brandId;
    if (selectedBrandIds.includes(brandId)) {
      setSelectedBrandIds((prev) => prev.filter((id) => id !== brandId));
      const allOtherForms = (brand.formTlc3Details || [])
        .map(d => d.formTlc3)
        .filter(f => f !== formTlc3);
      setSelectedFormTlcs((prev) => ({ ...prev, [brandId]: allOtherForms }));
      return;
    }

    setSelectedFormTlcs((prev) => {
      const current = prev[brandId] || [];
      const updated = current.includes(formTlc3)
        ? current.filter((item) => item !== formTlc3)
        : [...current, formTlc3];
      
      if (updated.length === 0) {
        const newState = { ...prev };
        delete newState[brandId];
        return newState;
      }
      return { ...prev, [brandId]: updated };
    });
  };

  const handleToggleExpand = (nodeId) => {
    setExpandedNodeIds((prev) => (
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]
    ));
  };

  const handleCreateMarket = async () => {
    if (!newMarket.marketName || !newMarket.marketType) {
      toast.error('Market name and market type are required.');
      return;
    }

    try {
      setCreatingMarket(true);
      const response = await execute(API_CONFIG.marketBaskets.createMarket, newMarket, {
        successMessage: 'Market created successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to create market.',
        headers: userName ? { user: userName } : undefined,
      });
      const createdId = response?.marketId?.marketId || response?.marketId;
      setDialogOpen(false);
      setNewMarket({ marketName: '', marketType: '' });
      await loadMarketsAndBrands();
      if (createdId) {
        setSelectedMarketId(String(createdId));
      }
    } catch {
    } finally {
      setCreatingMarket(false);
    }
  };

  const handleAddBrands = () => {
    if (!basketTree || !selectedNode) {
      toast.error('Select a basket node first.');
      return;
    }
    
    const hasBrandSelection = selectedBrandIds.length > 0;
    const hasFormTlcSelection = Object.keys(selectedFormTlcs).length > 0;

    if (!hasBrandSelection && !hasFormTlcSelection) {
      toast.error('Select at least one brand or form TLC to add.');
      return;
    }
    if (selectedNode.data?.hierarchyLevel === 'drug' || selectedNode.data?.hierarchyLevel === 'formTlc3') {
      toast.error('Please select a market or brand node.');
      return;
    }

    const involvedBrandIds = Array.from(new Set([...selectedBrandIds, ...Object.keys(selectedFormTlcs)]));

    const selectedBrands = brands
      .filter((brand) => involvedBrandIds.some((id) => String(id) === String(brand.brandId)))
      .map((brand) => {
        if (selectedBrandIds.some((id) => String(id) === String(brand.brandId))) {
          return brand;
        }
        const selectedForms = selectedFormTlcs[brand.brandId] || [];
        return {
          ...brand,
          formTlc3Details: (brand.formTlc3Details || []).filter((detail) => selectedForms.includes(detail.formTlc3)),
        };
      });

    const existingBrands = flattenBrands(basketTree, []);
    const selectedBrandNames = selectedBrands.map((brand) => brand.brandName);
    const duplicateBrands = selectedBrandNames.filter((brandName) => existingBrands.includes(brandName));
    const uniqueBrands = selectedBrands.filter((brand) => !duplicateBrands.includes(brand.brandName));
    const duplicatedBrandsFromSelected = selectedBrands.filter((brand) => duplicateBrands.includes(brand.brandName));
    const noDrugs = [];
    const duplicatedTlc3s = [];
    let addedBrandCount = 0;
    let addedFormCount = 0;

    if (selectedNode.data?.hierarchyLevel !== 'brand' && duplicateBrands.length > 0) {
      toast.error(`Brand ${duplicateBrands.join(', ')} already exists`);
    } else if (selectedNode.data?.hierarchyLevel === 'brand' && uniqueBrands.length > 0) {
      toast.error('New brand cannot be added to the existing brand');
    }

    const nextTree = updateNodeChildren(basketTree, selectedNodeId, (node) => {
      if (node.data?.hierarchyLevel === 'brand') {
        const matchingBrand = duplicatedBrandsFromSelected.find((brand) => brand.brandName === node.text);
        if (!matchingBrand) {
          return node;
        }

        const existingForms = (node.children || []).map((child) => child.text);
        const newForms = (matchingBrand.formTlc3Details || [])
          .filter((detail) => {
            const isDuplicate = existingForms.includes(detail.formTlc3);
            if (isDuplicate) {
              duplicatedTlc3s.push(detail.formTlc3);
            }
            return !isDuplicate;
          })
          .map((detail) => buildFormTlc3Node(detail, userName, { id: matchingBrand.brandName }));

        addedFormCount += newForms.length;
        return { ...node, children: [...(node.children || []), ...newForms] };
      }

      const newBrandNodes = uniqueBrands.flatMap((brand) => {
        if (!Array.isArray(brand.formTlc3Details) || brand.formTlc3Details.length === 0) {
          noDrugs.push(brand.brandName);
          return [];
        }

        addedBrandCount += 1;
        return [{
          ...buildBrandNode(brand, userName),
          children: brand.formTlc3Details.map((detail) => buildFormTlc3Node(detail, userName)),
        }];
      });

      return { ...node, children: [...(node.children || []), ...newBrandNodes] };
    });

    if (addedBrandCount === 0 && addedFormCount === 0) {
      if (selectedNode.data?.hierarchyLevel === 'brand') {
        toast.error('No new Form TLC3 values were found for the selected brand.');
      } else if (duplicateBrands.length > 0) {
        toast.error(`Brand ${duplicateBrands.join(', ')} already exists`);
      } else if (noDrugs.length > 0) {
        toast.error(`Brand ${noDrugs.join(', ')} has no drugs`);
      }
      return;
    }

    setBasketTree(nextTree);
    setExpandedNodeIds((prev) => (
      prev.includes(String(selectedNodeId)) ? prev : [...prev, String(selectedNodeId)]
    ));
    setSelectedBrandIds([]);
    setSelectedFormTlcs({});

    if (noDrugs.length > 0) {
      toast.error(`Brand ${noDrugs.join(', ')} has no drugs`);
    }
    if (duplicatedTlc3s.length > 0) {
      toast.info(`Form TLC3 ${duplicatedTlc3s.join(', ')} already exists`);
    }
  };

  const handleRemoveNode = () => {
    if (!basketTree || !selectedNode) {
      toast.error('Select a basket node first.');
      return;
    }
    if (selectedNode.data?.hierarchyLevel === 'market' || selectedNode.data?.hierarchyLevel === 'drug') {
      toast.error('Market and drug nodes cannot be removed.');
      return;
    }

    const parentNode = findParentNode(basketTree, selectedNodeId);
    const updated = removeNode(basketTree, selectedNodeId);
    setBasketTree(updated);
    setSelectedNodeId(String(parentNode?.data?.id ?? parentNode?.text ?? updated.data?.id ?? updated.text));
    setExpandedNodeIds((prev) => prev.filter((id) => id !== String(selectedNodeId)));
  };

  const handleDeleteSelectedNode = () => {
    handleCloseNodeMenu();
    handleRemoveNode();
  };

  const handleNodeSelect = (event, nodeId) => {
    setSelectedNodeId(nodeId);
    setNodeMenuAnchorEl(event.currentTarget);
  };

  const handleCloseNodeMenu = () => {
    setNodeMenuAnchorEl(null);
  };

  const handleOpenCreateNodeDialog = (nodeId = selectedNodeId) => {
    const targetNode = basketTree && nodeId ? findNode(basketTree, nodeId) : null;

    if (!basketTree || !targetNode) {
      toast.error('Select a market or class node first.');
      return;
    }

    if (!canCreateChildNode(targetNode)) {
      const level = targetNode.data?.hierarchyLevel;
      if (level === 'classLevel3') {
        toast.error('Class Level 3 cannot have child nodes.');
        return;
      }
      toast.error('Cannot create a node under the selected entry.');
      return;
    }

    setSelectedNodeId(String(nodeId));
    handleCloseNodeMenu();
    setNodeDialogMode('create');
    setNodeNameDraft('');
    setNodeDialogOpen(true);
  };

  const handleOpenRenameDialog = (nodeId = selectedNodeId) => {
    const targetNode = basketTree && nodeId ? findNode(basketTree, nodeId) : null;

    if (!basketTree || !targetNode) {
      toast.error('Select a class node first.');
      return;
    }

    if (!canRenameClassNode(targetNode)) {
      toast.error('Only class nodes can be renamed.');
      return;
    }

    setSelectedNodeId(String(nodeId));
    handleCloseNodeMenu();
    setNodeDialogMode('rename');
    setNodeNameDraft(targetNode.text || '');
    setNodeDialogOpen(true);
  };

  const handleCreateNode = () => {
    if (!nodeNameDraft.trim()) {
      toast.error('Node name is required.');
      return;
    }
    if (!selectedNode) return;

    const parentLevel = selectedNode.data?.hierarchyLevel;
    const nextLevel = parentLevel?.includes('classLevel') ? Number(parentLevel.slice(10)) + 1 : 1;
    const hierarchyLevel = `classLevel${nextLevel}`;
    const newNodeId = `new-${hierarchyLevel}-${Date.now()}`;
    const newNode = {
      text: nodeNameDraft.trim(),
      data: {
        id: newNodeId,
        action: 'ADD',
        hierarchyLevel,
        createdBy: userName,
      },
      children: [],
    };

    setBasketTree((prev) => updateNodeChildren(prev, selectedNodeId, (node) => ({
      ...node,
      children: [...(node.children || []), newNode],
    })));
    setSelectedNodeId(newNodeId);
    setExpandedNodeIds((prev) => (prev.includes(selectedNodeId) ? prev : [...prev, selectedNodeId]));
    setNodeDialogOpen(false);
    setNodeNameDraft('');
  };

  const handleRenameNode = () => {
    if (!nodeNameDraft.trim()) {
      toast.error('Node name is required.');
      return;
    }
    if (!selectedNode) return;

    setBasketTree((prev) => updateNodeText(prev, selectedNodeId, nodeNameDraft.trim(), userName));
    setNodeDialogOpen(false);
  };

  const handleOpenDrugDetails = () => {
    if (selectedNode?.data?.hierarchyLevel !== 'brand') {
      toast.error('Select a brand node to view drug details.');
      return;
    }

    setDrugDetailsOpen(true);
  };

  const handleToggleFormExcluded = (formNodeId, checked) => {
    if (!basketTree) return;

    setBasketTree((prev) => updateNodeData(prev, formNodeId, (data) => ({
      ...data,
      isExcluded: checked,
      updatedBy: userName || data.updatedBy,
      action: data.action || 'UPDATE',
    })));
  };

  const handleUpdateDrugField = (drugNodeId, field, value) => {
    if (!basketTree) return;

    setBasketTree((prev) => updateNodeData(prev, drugNodeId, (data) => ({
      ...data,
      [field]: value,
      updatedBy: userName || data.updatedBy,
      action: data.action || 'UPDATE',
    })));
  };

  const drugDetailColumns = useMemo(() => ([
    {
      header: 'NDC',
      id: 'ndc',
      accessorFn: (row) => row.data?.ndc || '-',
      size: 140,
      Cell: ({ row }) => renderEllipsisCell(row.original.data?.ndc || '-', 140),
    },
    {
      header: 'Drug Name',
      id: 'drugName',
      accessorFn: (row) => row.text || '-',
      size: 340,
      Cell: ({ row }) => renderEllipsisCell(row.original.text || '-', 340),
    },
    {
      header: 'Manufacturer',
      id: 'manufacturer',
      accessorFn: (row) => row.original?.data?.manufacturer || '-',
      size: 140,
      Cell: ({ row }) => renderEllipsisCell(row.original.data?.manufacturer || '-', 140),
    },
    {
      header: 'PGN Key',
      id: 'pgnKey',
      accessorFn: (row) => row.data?.pgnKey || '-',
      size: 110,
      Cell: ({ row }) => renderEllipsisCell(row.original.data?.pgnKey || '-', 110),
    },
    {
      header: 'PGN Description',
      id: 'pgnDesc',
      accessorFn: (row) => row.data?.pgnDesc || '-',
      size: 170,
      Cell: ({ row }) => renderEllipsisCell(row.original.data?.pgnDesc || '-', 170),
    },
    {
      header: 'Key Competitor',
      id: 'keyCompetitor',
      size: 120,
      Cell: ({ row }) => (
        <Select
          size="small"
          value={row.original.data?.keyCompetitor ? 'true' : 'false'}
          onChange={(event) => handleUpdateDrugField(row.original.data?.id, 'keyCompetitor', event.target.value === 'true')}
          sx={{ minWidth: 96 }}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      ),
    },
    {
      header: 'Client Drugs',
      id: 'clientDrugFlag',
      size: 120,
      Cell: ({ row }) => (
        <Select
          size="small"
          value={row.original.data?.clientDrugFlag ? 'true' : 'false'}
          onChange={(event) => handleUpdateDrugField(row.original.data?.id, 'clientDrugFlag', event.target.value === 'true')}
          sx={{ minWidth: 96 }}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      ),
    },
  ]), [handleUpdateDrugField]);

  const brandColumns = useMemo(() => ([
    {
      header: '',
      id: 'select',
      size: 48,
      maxSize: 48,
      enableSorting: false,
      Cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedBrandIds.includes(row.original.brandId)}
          onChange={() => toggleBrandSelection(row.original.brandId)}
        />
      ),
    },
    { header: 'Brand Name', id: 'brandName', accessorFn: (row) => row.brandName },
    { header: 'Brand ID', id: 'brandId', accessorFn: (row) => row.brandId },
  ]), [selectedBrandIds]);

  const handleSubmit = async () => {
    if (!selectedMarket || !basketTree) {
      toast.error('Select a market basket first.');
      return;
    }

    const emptyClassNames = collectEmptyClassNames(basketTree, []);
    if (emptyClassNames.length > 0) {
      toast.error(`Class ${emptyClassNames.join(', ')} cannot be empty.`);
      return;
    }

    try {
      setSaving(true);
      const preparedBasket = prepareBasketForSubmit(basketTree);
      preparedBasket.data = {
        ...preparedBasket.data,
        updatedBy: userName || preparedBasket.data.updatedBy,
      };
      const response = await execute(API_CONFIG.marketBaskets.updateMarketBasket, {
        market_id: selectedMarket.marketId,
        data: preparedBasket,
      }, {
        successMessage: `${selectedMarket.marketName} updated successfully.`,
        showSuccessToast: true,
        errorMessage: 'Unable to update market basket.',
        headers: userName ? { user: userName } : undefined,
      });
      if (response) {
        const normalized = normalizeBasketNode(response);
        setBasketTree(normalized);
        setSelectedNodeId(String(normalized.data?.id ?? normalized.text));
        setExpandedNodeIds(collectExpandedIds(normalized, []));
      }
    } catch {
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 1.5, alignItems: { xs: 'flex-start', md: 'flex-start' } }}>
          <Box>
            <Typography sx={{  fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
              Market Baskets
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              Management of market definitions and performance tracking baskets
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ ml: { md: 'auto' }, justifyContent: 'flex-end' }}>
            {/* <Button className="action-btn close-btn action-btn close-btn"
              variant="outlined"
              onClick={loadMarketsAndBrands}
              disabled={refreshingLists || saving}
              sx={mergeStyleSecondaryButtonSx}
            >
              {refreshingLists ? 'Refreshing...' : 'Refresh'}
            </Button> */}
            <Button className="action-btn process-btn action-btn process-btn"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={creatingMarket}
              sx={mergeStylePrimaryButtonSx}
            >
              New Market
            </Button>
            <Button className="action-btn process-btn action-btn process-btn"
              variant="contained"
              onClick={handleSubmit}
              disabled={!basketTree || saving}
              sx={mergeStyleInfoButtonSx}
            >
              {saving ? 'Saving...' : 'Submit'}
            </Button>
          </Stack>
        </Stack>

        {loadingPage ? (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Skeleton variant="rounded" width={320} height={40} animation="wave" sx={{ borderRadius: 2 }} />
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
              {/* Brands Table Skeleton */}
              <Paper elevation={0} sx={{ flex: '1 1 0', width: { xs: '100%', lg: 'calc((100% - 52px - 24px) / 2)' }, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={430} animation="wave" sx={{ borderRadius: 2 }} />
              </Paper>
              
              {/* Arrows Skeleton */}
              <Stack justifyContent="center" alignItems="center" spacing={1.5} sx={{ minWidth: { xs: '100%', lg: 52 } }}>
                <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: 2 }} />
                <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: 2 }} />
              </Stack>
              
              {/* Basket Skeleton */}
              <Paper elevation={0} sx={{ flex: '1 1 0', width: { xs: '100%', lg: 'calc((100% - 52px - 24px) / 2)' }, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Skeleton variant="text" width={140} height={32} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={520} animation="wave" sx={{ borderRadius: 2 }} />
              </Paper>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: '0px solid', borderColor: 'divider' }}>
              <FormControl sx={{ width: { xs: '100%', md: 320 } }} size="small">
                <InputLabel>Select Market</InputLabel>
                <Select
                  displayEmpty
                  value={selectedMarketId}
                  label="Select Market"
                  onChange={(event) => setSelectedMarketId(event.target.value)}
                  sx={{ height: 38 }}
                >
                  <MenuItem value="" disabled>Select Market Basket</MenuItem>
                  {markets.map((market) => (
                    <MenuItem key={market.marketId} value={String(market.marketId)}>
                      {market.marketName} ({market.marketType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch" sx={{ height: { lg: 500 } }}>
              <Paper
                elevation={0}
                sx={{
                  flex: { xs: '0 0 auto', lg: '1 1 0' },
                  minHeight: { xs: 500, lg: 0 },
                  width: { xs: '100%', lg: 'calc((100% - 52px - 24px) / 2)' },
                  minWidth: 0,
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <GlobalMaterialTable
                  columns={brandColumns}
                  data={brands}
                  page={brandTablePage}
                  pageSize={brandTablePageSize}
                  onPageChange={setBrandTablePage}
                  onPageSizeChange={setBrandTablePageSize}
                  manualPagination={false}
                  enablePagination={true}
                  enableBottomToolbar={true}
                  enableExpanding
                  icons={{
                    ExpandMoreIcon: AddIcon,
                    ExpandLessIcon: RemoveIcon,
                    KeyboardArrowDownIcon: RemoveIcon,
                    KeyboardArrowRightIcon: AddIcon,
                  }}
                  getRowCanExpand={(row) => Array.isArray(row.original.formTlc3Details) && row.original.formTlc3Details.length > 0}
                  renderDetailPanel={({ row }) => (
                    <NestedFormTlcList
                      brand={row.original}
                      formTlc3Details={row.original.formTlc3Details || []}
                      selectedBrandIds={selectedBrandIds}
                      selectedFormTlcs={selectedFormTlcs}
                      toggleFormTlcSelection={toggleFormTlcSelection}
                    />
                  )}
                  displayColumnDefOptions={{
                    'mrt-row-expand': {
                      size: 28,
                      maxSize: 28,
                      grow: false,
                    },
                    select: {
                      size: 48,
                      maxSize: 48,
                      grow: false,
                    },
                  }}
                  tableTitle="Brands"
                  tableCount={brands.length}
                  muiSearchTextFieldProps={{
                    placeholder: 'Search',
                  }}
                  muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
                  muiTableContainerProps={{ sx: { flex: 1, overflow: 'auto', minHeight: 0 } }}
                />
              </Paper>

              <Stack
                justifyContent="center"
                alignItems="center"
                spacing={1.5}
                direction={{ xs: 'row', lg: 'column' }}
                sx={{
                  marginLeft: { xs: '0px !important', lg: '1px !important' },
                  flexShrink: 0,
                  minWidth: { xs: '100%', lg: 52 },
                  minHeight: { xs: 52, lg: 'auto' },
                  alignSelf: 'stretch',
                }}
              >
                <IconButton
                  onClick={handleAddBrands}
                  disabled={loadingBasket || saving}
                  sx={{
                    width: 35,
                    height: 35,
                    border: '1px solid',
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(30, 136, 229, 0.2)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                      boxShadow: '0 4px 8px rgba(30, 136, 229, 0.3)',
                      transform: 'translateX(1px)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f1f5f9',
                      borderColor: '#e2e8f0',
                      color: '#94a3b8',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                >
                  <ChevronRightIcon fontSize="small" sx={{ transform: { xs: 'rotate(90deg)', lg: 'none' } }} />
                </IconButton>
                <IconButton
                  onClick={handleRemoveNode}
                  disabled={loadingBasket || saving}
                  sx={{
                    width: 35,
                    height: 35,
                    border: '1px solid',
                    borderColor: '#fca5a5',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.05)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: '#fecaca',
                      borderColor: '#f87171',
                      boxShadow: '0 4px 8px rgba(220, 38, 38, 0.1)',
                      transform: 'translateX(-1px)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f1f5f9',
                      borderColor: '#e2e8f0',
                      color: '#94a3b8',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                >
                  <ChevronRightIcon fontSize="small" sx={{ transform: { xs: 'rotate(-90deg)', lg: 'rotate(180deg)' } }} />
                </IconButton>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  flex: { xs: '0 0 auto', lg: '1 1 0' },
                  minHeight: { xs: 500, lg: 0 },
                  width: { xs: '100%', lg: 'calc((100% - 52px - 24px) / 2)' },
                  marginLeft: { xs: '0px !important', lg: '1px !important' },
                  minWidth: 0,
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={1.5}
                  sx={{ mb: 2, flexShrink: 0, width: '100%' }}
                >
                  <Typography sx={{  fontWeight: 700, color: '#1e293b' }}>
                    Current Basket
                  </Typography>
                </Stack>

                {loadingBasket ? (
                  <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2, border: '1px solid #f1f5f9', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                    {[...Array(6)].map((_, index) => (
                      <Box key={`skeleton-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: index > 0 && index < 4 ? 3 : index >= 4 ? 6 : 0 }}>
                        <Skeleton variant="circular" width={20} height={20} animation="wave" sx={{ opacity: 0.6 }} />
                        <Skeleton variant="text" width={140 + (index % 3) * 40} height={24} animation="wave" sx={{ opacity: 0.6 }} />
                      </Box>
                    ))}
                  </Box>
                ) : !basketTree ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1.5px dashed #cbd5e1',
                      borderRadius: '8px',
                      backgroundColor: '#f8fafc',
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Inventory2OutlinedIcon sx={{  color: '#94a3b8', mb: 1.5, opacity: 0.8 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569', mb: 0.5 }}>
                      No Basket Loaded
                  </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', maxWidth: 280, lineHeight: 1.4 }}>
                      Select a market basket from the dropdown above to view and manage its definition.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'auto',
                      minWidth: 0,
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      p: 1,
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <BasketNode
                      node={basketTree}
                      level={0}
                      selectedNodeId={selectedNodeId}
                      onSelect={handleNodeSelect}
                      expandedNodeIds={expandedNodeIds}
                      onToggleExpand={handleToggleExpand}
                      isLast
                    />
                  </Box>
                )}
              </Paper>
            </Stack>

          </Stack>
        )}
      </Paper>

      <Menu
        anchorEl={nodeMenuAnchorEl}
        open={Boolean(nodeMenuAnchorEl)}
        onClose={handleCloseNodeMenu}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            handleCloseNodeMenu();
            handleOpenDrugDetails();
          }}
          disabled={loadingBasket || saving || selectedNode?.data?.hierarchyLevel !== 'brand'}
        >
          View Drug Details
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenCreateNodeDialog()}
          disabled={loadingBasket || saving || !canCreateChildNode(selectedNode)}
        >
          Create Node
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenRenameDialog()}
          disabled={loadingBasket || saving || !canRenameClassNode(selectedNode)}
        >
          Rename Node
        </MenuItem>
        <MenuItem
          onClick={handleDeleteSelectedNode}
          disabled={loadingBasket || saving || !canDeleteNode(selectedNode)}
          sx={{ color: canDeleteNode(selectedNode) ? 'error.main' : undefined }}
        >
          Delete Node
        </MenuItem>
      </Menu>

      <Dialog open={nodeDialogOpen} onClose={() => setNodeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={marketDialogTitleSx}>{nodeDialogMode === 'create' ? 'Create Class Node' : 'Rename Node'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={nodeDialogMode === 'create' ? 'New Node Name' : 'Node Name'}
              placeholder={nodeDialogMode === 'create' ? 'Enter new node name' : 'Enter new name'}
              value={nodeNameDraft}
              onChange={(event) => setNodeNameDraft(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={marketDialogActionsSx}>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={() => setNodeDialogOpen(false)} disabled={creatingMarket} variant="outlined" sx={mergeStyleSecondaryButtonSx}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={nodeDialogMode === 'create' ? handleCreateNode : handleRenameNode} sx={mergeStylePrimaryButtonSx}>
            {nodeDialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={marketDialogTitleSx}>Create Market</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  color: '#334155',
                }}
              >
              Market Name
              </Typography>
            <TextField
              placeholder="Enter Market Name"
              value={newMarket.marketName}
              onChange={(event) => setNewMarket((prev) => ({
                ...prev,
                marketName: event.target.value,
                createdBy: userName || prev.createdBy,
              }))}
              fullWidth
            />
            </Box>
            <FormControl fullWidth>
               <Typography
                variant="body2"
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  color: '#334155',
                }}
              >
              Market Type
              </Typography>
              <Select
                displayEmpty
                value={newMarket.marketType}
                onChange={(event) => setNewMarket((prev) => ({
                  ...prev,
                  marketType: event.target.value,
                  createdBy: userName || prev.createdBy,
                }))}
              >
                <MenuItem value="" disabled>Select Market Type</MenuItem>
                <MenuItem value="XPT">XPT</MenuItem>
                <MenuItem value="NPA">NPA</MenuItem>
                <MenuItem value="CUSTOM">CUSTOM</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={marketDialogActionsSx}>
          <Button className="action-btn cancel-btn action-btn cancel-btn" onClick={() => setDialogOpen(false)} disabled={creatingMarket} variant="outlined" sx={mergeStyleSecondaryButtonSx}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={handleCreateMarket} disabled={creatingMarket} sx={mergeStylePrimaryButtonSx}>
            {creatingMarket ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={drugDetailsOpen} onClose={() => setDrugDetailsOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle sx={marketDialogTitleSx}>Drug Details</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '80vh' }}>
          <Stack spacing={3}>
            {selectedBrandDrugGroups.map((formGroup) => (
              <Box key={String(formGroup.data?.id ?? formGroup.text)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Form TLC3 - {formGroup.text}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <input
                      type="checkbox"
                      checked={Boolean(formGroup.data?.isExcluded)}
                      onChange={(event) => handleToggleFormExcluded(formGroup.data?.id ?? formGroup.text, event.target.checked)}
                    />
                    <Typography variant="body2">Exclude</Typography>
                  </Stack>
                </Stack>
                <Box sx={{ height: 340, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <GlobalMaterialTable
                    columns={drugDetailColumns}
                    data={formGroup.children || []}
                    emptyMessage="No drugs available"
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableGlobalFilter={false}
                    enableColumnFilters={false}
                    enableDensityToggle={false}
                    enableColumnActions={false}
                    enableHiding={false}
                    enableFullScreenToggle={false}
                    enableExpanding
                    getRowCanExpand={(row) => Array.isArray(row.original.data?.factors) && row.original.data.factors.length > 0}
                    renderDetailPanel={({ row }) => (
                      <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50' }}>
                        <Box sx={{ height: 180, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', backgroundColor: '#fff' }}>
                          <GlobalMaterialTable
                            columns={factorColumns}
                            data={row.original.data?.factors || []}
                            emptyMessage="No factor rows available"
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enablePagination={false}
                            enableGlobalFilter={false}
                            enableColumnFilters={false}
                            enableDensityToggle={false}
                            enableColumnActions={false}
                            enableHiding={false}
                            enableFullScreenToggle={false}
                            muiTablePaperProps={{ sx: { height: '100%', borderRadius: 0 } }}
                            muiTableContainerProps={{ sx: { height: 180, maxHeight: 180, overflow: 'auto' } }}
                          />
                        </Box>
                      </Box>
                    )}
                    displayColumnDefOptions={{
                      'mrt-row-expand': {
                        size: 28,
                        maxSize: 28,
                        grow: false,
                      },
                    }}
                    muiTablePaperProps={{ sx: { height: '100%', borderRadius: 0 } }}
                    muiTableContainerProps={{ sx: { height: 285, maxHeight: 285, overflow: 'auto' } }}
                  />
                </Box>
              </Box>
            ))}
            <Typography variant="body2" color="text.secondary">
              Note: Updating the Client Drug will also impact all markets that contain the same drug.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={marketDialogActionsSx}>
          <Button className="action-btn cancel-btn action-btn cancel-btn" variant="outlined" onClick={() => setDrugDetailsOpen(false)} sx={mergeStyleSecondaryButtonSx}>Cancel</Button>
          <Button className="action-btn process-btn action-btn process-btn" variant="contained" onClick={() => setDrugDetailsOpen(false)} sx={mergeStylePrimaryButtonSx}>Update</Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={saving}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography variant="body2">Saving market basket...</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
};

export default MarketBaskets;
